import { Box, Grid, lighten, ListItemButton, Typography } from '@mui/material'
import { AddIcon, ArrowAllCalendars, LayersIcon } from 'assets/icons'

import { useCalendar } from 'components/providers/CalendarProvider'
import { AnimatePresence, Reorder } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { withObservables } from '@nozbe/watermelondb/react'
import Database from 'services/db/Database'
import ObserveService from 'services/db/Observe'
import {
  AddTempGroup,
  GroupFilter,
  RemoveTempGroups,
  allGroupsForUI
} from 'services/db/Queries/GroupQueries'
import {
  AccountDoc,
  EventDoc,
  EventStatus,
  GroupDoc,
  LastMessage,
  MasterLabelDoc,
  MasterLabelLabel,
  UserSettingsDoc
} from 'types/api/changes'
import { Model } from 'types/model'

import * as Styled from './styled'
import { theme } from 'theme'
import { useConnection } from 'components/providers/ConnectionProvider'
import Analytics, { Events } from 'analytics/Analytics'
import { useTranslation } from 'react-i18next'
import useFitText from 'use-fit-text'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import GroupItemContextMenu from 'components/contextMenu/GroupItemContextMenu'
import { SegmentItem } from 'components/buttons/SegmentButton'
import { AppUIType, useAppUIType } from 'components/providers/AppUITypeProvider'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { GroupsList } from './GroupsList'
import LabelsList from './LabelsList'
import { addTempLabel } from 'services/db/Queries/MasterLabel'
import LabelItemContextMenu from 'components/contextMenu/LabelContextMenu'
import SearchPopupButton from 'components/SearchPopupButton'
import { SEARCH_MODE } from 'components/popups/searchItems/SearchPopup'
import { useTour } from '@reactour/tour'
import {
  ONBOARDING_MODE,
  useTfmTourProvider
} from 'components/onboarding/TFMTourProvider'
import HorizontalChipsBar, { ChipData } from './HorizontalChips'
import usePersistState from 'hooks/usePersistState'
import { allCalendarItems } from 'services/db/Queries/Event'
import { buildEventsArrayForUIFromDBEventsWithRecurrentEvents } from 'utils/api/events'
import dayjs from 'dayjs'
import { APP_COLOR } from 'utils/colors'
import TouchRipple from '@mui/material/ButtonBase/TouchRipple'
import { GroupCardButton, GroupCardItem } from 'components/cards/styled'
import { t } from 'i18next'
import { useLandlineCheck } from 'components/providers/LandlineCheckProvider'
import { isUserOutOfTierBasedOnLandline } from 'utils/tier'
import { groupToProvider, isConvertedGroup, ProviderType } from 'utils/groups'
import { useCalendarSync } from 'components/providers/CalendarSyncProdivder'
import Logger from 'services/Logger'

export interface LastUpdateMessagePerGroup {
  groupId: string
  lastUpdateMessage: LastMessage
}

export interface GroupsProps {
  masterLabel?: Model<MasterLabelDoc>
  groups?: Model<GroupDoc>[] | GroupDoc[]
  topGroups?: Model<GroupDoc>[]
  userSettings?: Model<UserSettingsDoc> | UserSettingsDoc
  account?: Model<AccountDoc>
}

export interface GroupContextMenuProps {
  x: number
  y: number
  group: GroupDoc
}

interface LabelContextMenuProps {
  x: number
  y: number
  label: MasterLabelLabel
}

function Groups(props: GroupsProps) {
  const landlineCheck = useLandlineCheck()
  const uiTypeContext = useAppUIType()
  const tour = useTour()
  const tfmTour = useTfmTourProvider()
  const calendar = useCalendar()
  const connection = useConnection()
  const [lastUpdateMessages, setLastUpdateMessages] = useState<
    LastUpdateMessagePerGroup[]
  >([])
  const [saving, setSaving] = useState(false)
  const [pendingGroup, setPendingGroup] = useState<
    Model<GroupDoc> | undefined
  >()
  const [addGroupPopupState, onAddGroupPopupState] = useState(false)
  const [groupFilter, setGroupFilter] = usePersistState(
    GroupFilter.LastUpdated.valueOf(),
    LocalStorageKey.GROUP_FILTER
  )
  const topRef = useRef(null)
  const listRef = useRef(null)

  const onCancelAdd = () => {
    RemoveTempGroups()
    onAddGroupPopupState(false)
    LocalStorage.remove(LocalStorageKey.ADDING_GROUP)
    setPendingGroup(undefined)
  }

  function handleAddGroup() {
    if (isUserOutOfTierBasedOnLandline(props.account)) {
      landlineCheck.onShowOutOfTierBasedOnLandline(true)
      return
    }
    if (connection.isConnectionAvailable()) {
      if (is24meApp()) {
        //add label here
        addTempLabel()
      } else {
        tour.setIsOpen(false)
        if (addGroupPopupState) return
        ;(listRef.current as HTMLDivElement | null)?.scrollTo(0, 0)
        Analytics.getInstance().sendEvent(Events.ADD_GROUP)
        addTempGroupToDB().then((group) => {
          console.log('added group to db', group)
          onAddGroupPopupState(true)
          setPendingGroup(group)
        })
      }
    }
  }

  const showAllCalendars = () => {
    uiTypeContext.setSelectedLabel(undefined)
    calendar.onGroupServer('')
    LocalStorage.remove(LocalStorageKey.PENDING_EVENT_DATA)
    onCancelAdd()

    if (tour.setSteps && tfmTour.getStepsByMode) {
      const steps = tfmTour.getStepsByMode(ONBOARDING_MODE.FIRST_LOGIN)
      if (tfmTour.isPlaying) return
      tfmTour.setMode(ONBOARDING_MODE.FIRST_LOGIN)
      tour.setSteps(steps)
      tour.setCurrentStep(0)
      if (steps.length > 0) {
        tour.setIsOpen(true)
      }
    }
  }

  const { t, i18n } = useTranslation()

  let groupsForUI: GroupDoc[] = allGroupsForUI(
    props.groups ?? [],
    props.userSettings,
    groupFilter === GroupFilter.Archived,
    groupFilter
  ).sort((a, b) => {
    const providerA = groupToProvider(a)
    const providerB = groupToProvider(b)

    return b._sortOrder - a._sortOrder
  })

  useEffect(() => {
    if (groupFilter === GroupFilter.NextEvent) {
      allCalendarItems().then((events) => {
        let allEvents = buildEventsArrayForUIFromDBEventsWithRecurrentEvents(
          events,
          dayjs().subtract(7, 'day'),
          dayjs().add(7, 'day')
        )

        let lastMessages: LastUpdateMessagePerGroup[] = []
        groupsForUI.forEach((group) => {
          // find closest event to now in the group
          let closestEvent: EventDoc | undefined = undefined
          let closestEventTime: number = Number.MAX_SAFE_INTEGER
          for (let i = 0; i < allEvents.length; i++) {
            const event = allEvents[i]
            if (event.GroupID === group._id) {
              const eventTime = Number(event.StartDate)
              if (
                eventTime < closestEventTime &&
                eventTime > dayjs().unix() &&
                event.Status === EventStatus.ACTIVE
              ) {
                closestEvent = event
                closestEventTime = eventTime
              }
            }
          }

          if (closestEvent) {
            const LastUpdateMessagePerGroup: LastUpdateMessagePerGroup = {
              groupId: group._id ?? '',
              lastUpdateMessage: {
                type: closestEvent.Text ?? '',
                when: dayjs(Number(closestEvent.StartDate) * 1000).format('lll')
              }
            }
            lastMessages.push(LastUpdateMessagePerGroup)
          } else {
            const LastUpdateMessagePerGroup: LastUpdateMessagePerGroup = {
              groupId: group._id ?? '',
              lastUpdateMessage: {
                type: t('noEventsComingUp')
              }
            }
            lastMessages.push(LastUpdateMessagePerGroup)
          }
        })

        // resort groups by closest event
        groupsForUI = groupsForUI.sort((a, b) => {
          const groupA = lastMessages.find((m) => m.groupId === a._id)
          const groupB = lastMessages.find((m) => m.groupId === b._id)
          if (groupA && groupB) {
            return (
              dayjs(groupB.lastUpdateMessage.when).unix() -
              dayjs(groupA.lastUpdateMessage.when).unix()
            )
          }
          return 0
        })

        setLastUpdateMessages(lastMessages)
      })
    } else {
      setLastUpdateMessages([])
    }
  }, [groupFilter])

  const [contextMenuPosition, setContextMenuPosition] =
    useState<GroupContextMenuProps | null>(null)
  const [contextMenuPositionLabel, setContextMenuPositionLabel] =
    useState<LabelContextMenuProps | null>(null)

  const containerRef = useRef<HTMLDivElement>(null)
  const handleCloseContextMenu = () => {
    setContextMenuPosition(null)
    setContextMenuPositionLabel(null)
    LocalStorage.remove(LocalStorageKey.CONTEXT_FOR_GROUP)
  }

  const handleContextMenu = (
    event: React.MouseEvent<HTMLElement>,
    group: GroupDoc
  ) => {
    event.preventDefault() // Prevent the default context menu from showing
    handleCloseContextMenu() // Close the existing context menu

    const containerRect = containerRef?.current?.getBoundingClientRect()
    if (!containerRect) return

    const offsetX =
      event.clientX -
      (i18n.dir() === 'rtl' ? containerRect.right : containerRect.left)
    const offsetY = event.clientY - containerRect.top

    setContextMenuPosition({ x: offsetX, y: offsetY, group })
  }

  const handleLabelContextMenu = (
    event: React.MouseEvent<HTMLLIElement>,
    label: MasterLabelLabel
  ) => {
    event.preventDefault() // Prevent the default context menu from showing
    handleCloseContextMenu() // Close the existing context menu

    const containerRect = containerRef.current?.getBoundingClientRect()
    if (!containerRect) return

    const offsetX =
      event.clientX -
      (i18n.dir() === 'rtl' ? containerRect.right : containerRect.left)
    const offsetY = event.clientY - containerRect.top

    setContextMenuPositionLabel({ x: offsetX, y: offsetY, label })
  }

  const calendarSync = useCalendarSync()

  return (
    <Styled.Groups id="first-group">
      <Grid
        gap={'0.5rem'}
        display={'flex'}
        flexDirection={'row'}
        alignItems={'center'}
        width={'100%'}
      >
        <SearchPopupButton
          keepBorder
          textfieldPlaceholder={`${
            is24meApp()
              ? t('searchItems', { what: t('eventsTasksNotes').toLowerCase() })
              : t('searchEvents')
          }`}
          mode={is24meApp() ? SEARCH_MODE.OnlyAllEvents : SEARCH_MODE.All}
        />
        <GroupCardItem
          style={{
            border: `1px solid rgba(177, 186, 200, ${
              !calendar.groupServerID && !uiTypeContext.selectedLabel ? 1 : 0.25
            })`,
            borderRadius: '24px'
          }}
        >
          <GroupCardButton
            id="all-calendars-button"
            selected={!calendar.groupServerID && !uiTypeContext.selectedLabel}
            sx={{
              borderRadius: '24px',
              '&.Mui-selected': {
                backgroundColor: `${lighten(APP_COLOR, 0.8)} !important` // Change this to your desired selected color
              }
            }}
            onClick={() => {
              showAllCalendars()
            }}
          >
            <Grid item alignItems={'center'} display={'flex'}>
              <LayersIcon />
            </Grid>
            {AllCalendarsDiv()}
            <ArrowAllCalendars
              style={{
                transform: i18n.dir() === 'ltr' ? '' : 'rotate(180deg)'
              }}
            />
          </GroupCardButton>
        </GroupCardItem>
      </Grid>
      {isGroupcalApp() && (
        <HorizontalChipsBar
          chips={[
            {
              title: t('lastUpdated'),
              key: GroupFilter.LastUpdated
            },
            {
              title: t('nextEvent'),
              key: GroupFilter.NextEvent
            },
            {
              title: t('unread'),
              key: GroupFilter.Unread
            },
            {
              title: t('archived'),
              key: GroupFilter.Archived
            }
          ]}
          selectedKey={groupFilter}
          onChipSelect={function (selectedChip: ChipData): void {
            setGroupFilter(selectedChip.key)
          }}
        />
      )}
      <Styled.GroupList
        ref={containerRef}
        sx={{
          marginTop: is24meApp() ? '1rem' : 0,
          '&& .Mui-selected': {
            bgcolor: theme.palette.button.background.selected
          }
        }}
      >
        <Box>
          {groupsForUI.length === 0 &&
          groupFilter !== GroupFilter.LastUpdated ? (
            <Typography marginTop={'20px'} textAlign={'center'}>
              {t('nothingToShowHere')}
            </Typography>
          ) : (
            <Styled.GroupListScrollContainer
              sx={{
                marginRight: i18n.dir() === 'ltr' ? 0 : theme.spacing(-5),
                marginLeft: i18n.dir() === 'ltr' ? theme.spacing(-5) : 0
              }}
            >
              <Styled.GroupListScroll ref={listRef}>
                <AnimatePresence>
                  <Reorder.Group
                    axis="y"
                    as="div"
                    values={groupsForUI.map((row) => row.id)}
                    onReorder={() => {}}
                  >
                    {(isGroupcalApp() ||
                      uiTypeContext.uiType.value === AppUIType.calendars) && (
                      <GroupsList
                        account={props.account}
                        userSettings={props.userSettings}
                        saving={saving}
                        groupsForUI={groupsForUI ?? []}
                        setSaving={setSaving}
                        pendingGroup={pendingGroup}
                        addGroupPopupState={addGroupPopupState}
                        onAddGroupPopupState={onAddGroupPopupState}
                        onCancelAdd={onCancelAdd}
                        handleCloseContextMenu={handleCloseContextMenu}
                        handleContextMenu={handleContextMenu}
                        topRef={topRef}
                        lastUpdateMessages={lastUpdateMessages}
                      />
                    )}
                    {is24meApp() &&
                      uiTypeContext.uiType.value != AppUIType.calendars && (
                        <LabelsList
                          masterLabel={props.masterLabel}
                          handleCloseContextMenu={handleCloseContextMenu}
                          handleContextMenu={handleLabelContextMenu}
                        />
                      )}
                  </Reorder.Group>
                </AnimatePresence>
                <Box height={60} />
              </Styled.GroupListScroll>
            </Styled.GroupListScrollContainer>
          )}
          {(isGroupcalApp() ||
            uiTypeContext.uiType.value != AppUIType.calendars) && (
            <Styled.AddGroupButton
              id="add-group-button"
              onClick={handleAddGroup}
            >
              <AddIcon />
              <span>
                {t(
                  uiTypeContext.uiType.value === AppUIType.calendars
                    ? 'calendarButton'
                    : 'label'
                )}
              </span>
            </Styled.AddGroupButton>
          )}
          {is24meApp() &&
            uiTypeContext.uiType.value === AppUIType.calendars && (
              <Styled.AddGroupButton
                id="add-group-button"
                onClick={() => {
                  if (calendarSync.showProviderPicker) {
                    calendarSync.showProviderPicker()
                  }
                }}
              >
                <AddIcon />
                <span>{t('calendarButton')}</span>
              </Styled.AddGroupButton>
            )}
        </Box>
        {contextMenuPosition && (
          <GroupItemContextMenu
            account={props.account}
            userSettings={props.userSettings}
            x={contextMenuPosition?.x ?? 0}
            y={contextMenuPosition?.y ?? 0}
            group={contextMenuPosition.group}
            onClose={handleCloseContextMenu}
            contextMenuPosition={contextMenuPosition}
          />
        )}
        {contextMenuPositionLabel && (
          <LabelItemContextMenu
            userSettings={props.userSettings}
            x={contextMenuPositionLabel?.x ?? 0}
            y={contextMenuPositionLabel?.y ?? 0}
            label={contextMenuPositionLabel.label}
            onClose={handleCloseContextMenu}
            masterLabel={props.masterLabel}
          />
        )}
      </Styled.GroupList>
    </Styled.Groups>
  )
}

function AllCalendarsDiv() {
  const uiTypeContext = useAppUIType()
  const [maxFontSize, onMaxFontSize] = useState(120)
  const { fontSize, ref } = useFitText({
    minFontSize: 10,
    maxFontSize: 120
  })
  useEffect(() => {
    onMaxFontSize(uiTypeContext.getCurrentIndex() === 0 ? 120 : 100)
  }, [uiTypeContext.getCurrentIndex()])

  function allItemsButtonTitle(uiType: SegmentItem): string {
    switch (uiType.value) {
      case AppUIType.calendars:
        return t('allCalendarsButton')
      case AppUIType.notes:
        return t('allNotes')
      case AppUIType.tasks:
        return t('allTasks')
    }

    return ''
  }

  return (
    <div
      ref={ref}
      style={{
        marginLeft: '8px',
        marginRight: '8px',

        fontSize: fontSize,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        flex: '1',
        maxWidth: '160px',
        width: '160px !important'
      }}
    >
      {allItemsButtonTitle(uiTypeContext.uiType)}
    </div>
  )
}

export default withObservables([], () => ({
  masterLabel: ObserveService.MasterLabel(),
  groups: ObserveService.Groups(),
  account: ObserveService.Account(),
  topGroups: ObserveService.TopGroups(),
  userSettings: ObserveService.UserSettings()
}))(Groups)

function addTempGroupToDB() {
  return Database.write(async () => {
    return AddTempGroup()
  })
}
